import { connectionName } from '@owc/connection-aem-environment';
import {
  AUTHENTICATION_CONNECTION,
  AuthenticationState,
  getJwe,
} from '@b2x/authentication-library';
import {
  connectionName as aemConnectionName,
  ConnectionAemEnvironmentState,
} from '@owc/connection-aem-environment';

import { initializeStore } from '@seamless/store';

import { Maybe } from '../graphql/schema';

import Locale from '../models/utilities/locale.model';

import { useStore } from '../composables/store.composable';

import { TranslationService } from './translation.service';

import {
  connectionName as wkoConnectionName,
  WellKnownObjects,
} from '@owc/connection-wko-manager';

export const AEM_FORMS_SEAMLESS_ID = 'AEMF_FORM_CONTAINER';

export default class SeamlessStoreService {
  private static store = initializeStore();
  static async getStore(locale: Locale): Promise<void> {
    const { state } = useStore();

    if (!state.configuration?.isStandalone) {
      locale =
        (await this.getLocaleFromSeamless()) ||
        this.getLocaleFromAemConfig(locale);
    }
    await TranslationService.setLocale(locale);
  }

  private static getLocaleFromSeamless(): Promise<Locale | void> {
    return new Promise((resolve, reject) => {
      SeamlessStoreService.store.subscribe(
        connectionName,
        async (state: any) => {
          let locale: Locale | undefined;
          if (state && state.country && state.language) {
            locale = {
              countryCode: state.country,
              language: state.language,
            };
          }
          resolve(locale);
        }
      );
    });
  }

  static getLocaleFromAemConfig(locale: Locale): Locale {
    let countryCode = locale.countryCode;
    let language = locale.language;
    const aemConfig = window.sessionStorage.getItem(
      'ow_aem_apiGatewayConfiguration'
    );
    const aemConfigObject: any = aemConfig ? JSON.parse(aemConfig) : '';
    if (aemConfigObject.locale) {
      const countryCodeConfig = aemConfigObject.locale.split('-');
      countryCode = countryCodeConfig[1];
      language = countryCodeConfig[0];
    }
    return { countryCode, language };
  }

  static getB2XStore(): void {
    let token = sessionStorage.getItem('ow_aem_jwe');
    SeamlessStoreService.store.once(
      AUTHENTICATION_CONNECTION,
      (state: AuthenticationState) => {
        const jwe = getJwe(state);
        if (jwe) {
          token = jwe;
        }
      }
    );
    SeamlessStoreService.setToken(token);
  }

  static getWkoUser(): void {
    const wko = window.sessionStorage.getItem('ow_aem_wkoUserData');
    let userObject: any = wko ? JSON.parse(wko) : '';

    SeamlessStoreService.store.subscribe(
      wkoConnectionName,
      (state: WellKnownObjects) => {
        userObject = state.User?.personal?.wkoUserData;

        const b2XCoreAuthentication = (
          SeamlessStoreService.store as any
        ).store.getState().B2XCORE_AUTHENTICATION?.profile;

        SeamlessStoreService.setB2XCoreProfile(b2XCoreAuthentication);
      }
    );

    SeamlessStoreService.setWkoUserData(userObject);
  }

  static getApiUri(): string {
    let apiUri = '';
    SeamlessStoreService.store.once(
      aemConnectionName,
      (state: ConnectionAemEnvironmentState) => {
        apiUri = state.apiUri;
      }
    );
    return apiUri;
  }

  static setToken(token: Maybe<string>): void {
    const { commit } = useStore();
    commit('token', token);
  }

  static setWkoUserData(wkoUserData: Maybe<any>): void {
    const { commit } = useStore();
    commit('wkoUserData', wkoUserData);
  }

  static setB2XCoreProfile(b2xCoreProfile: Maybe<any>): void {
    const { commit } = useStore();
    commit('b2xCoreProfile', b2xCoreProfile);
  }

  static async initSeamlessStore(locale: Locale): Promise<void> {
    await SeamlessStoreService.getStore(locale);
    await SeamlessStoreService.getB2XStore();
    await SeamlessStoreService.getWkoUser();
  }
}
